import { useCallback, useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const useAnimatedAmount = (targetMinor, { duration = 1400 } = {}) => {
  const [displayMinor, setDisplayMinor] = useState(targetMinor || 0);
  const frameRef = useRef(null);
  const displayRef = useRef(displayMinor);

  useEffect(() => {
    displayRef.current = displayMinor;
  }, [displayMinor]);

  useEffect(() => {
    if (frameRef.current) {
      return;
    }
    setDisplayMinor(targetMinor || 0);
  }, [targetMinor]);

  const animateTo = useCallback(
    (toMinor, { ms = duration } = {}) => {
      const from = displayRef.current;
      const to = Number(toMinor) || 0;
      if (from === to) {
        setDisplayMinor(to);
        return Promise.resolve(to);
      }

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      const start = performance.now();

      return new Promise((resolve) => {
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / ms);
          const value = Math.round(from + (to - from) * easeOutCubic(progress));
          setDisplayMinor(value);
          if (progress < 1) {
            frameRef.current = requestAnimationFrame(tick);
          } else {
            frameRef.current = null;
            setDisplayMinor(to);
            resolve(to);
          }
        };
        frameRef.current = requestAnimationFrame(tick);
      });
    },
    [duration]
  );

  useEffect(
    () => () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    },
    []
  );

  return { displayMinor, animateTo, setDisplayMinor };
};
